/*
 * TICK-141 — degraded offline check-in: the pure half.
 *
 * **What "degraded" means, stated once.** A gate that loses the network keeps admitting people, and
 * every admission it makes while offline is *provisional*: recorded locally, replayed against the
 * server on reconnect, and only then a fact. The server stays the sole authority on whether an
 * order has already been admitted, because it is the only thing both devices can see.
 *
 * Three things this cannot do offline, all of them consequences of the same snapshot:
 *
 *   - **Notice a refund or a revocation** made since the bundle was taken.
 *   - **Notice an admission on another device.** Two gates offline can both admit one order. The
 *     server refuses the second replay, and that refusal becomes an exception a person resolves.
 *   - **Verify a QR the bundle has never seen.** A credential issued after the snapshot is unknown
 *     to it, and the desk says so instead of guessing.
 *
 * Everything here is pure or storage — no React, no fetch — so the rules above are testable as
 * data, and the hook is left with the network and the clock.
 */
import type { OfflineCheckinBundleDto, OfflineCheckinOrderDto } from '../../../api';

export type { OfflineCheckinBundleDto, OfflineCheckinOrderDto };

export interface OfflineIndex {
  generatedAt: string;
  orderCount: number;
  byCode: Map<string, OfflineCheckinOrderDto>;
  /** `SHA-256(payload)` → order. What lets a scanned QR resolve with no server and no secret. */
  byCredentialHash: Map<string, OfflineCheckinOrderDto>;
}

/** Order codes are shown uppercase and typed however people type them. */
const normalizeCode = (raw: string) => raw.trim().toUpperCase();

export function indexBundle(bundle: OfflineCheckinBundleDto): OfflineIndex {
  const byCode = new Map<string, OfflineCheckinOrderDto>();
  const byCredentialHash = new Map<string, OfflineCheckinOrderDto>();
  for (const order of bundle.orders) {
    byCode.set(normalizeCode(order.code), order);
    for (const hash of order.credentialHashes) byCredentialHash.set(hash, order);
  }
  return { generatedAt: bundle.generatedAt, orderCount: bundle.orderCount, byCode, byCredentialHash };
}

/** Hex SHA-256, matching what the bundle carries. */
export async function hashPayload(payload: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(payload));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface QueuedAdmission {
  /** Generated once, at admission. Carried through replay so a retry cannot admit twice. */
  id: string;
  code: string;
  /** Replayed verbatim — the server resolves it exactly as it would have online. */
  payload: string;
  name: string;
  entries: number;
  /** When the operator actually confirmed, not when it reached the server. */
  admittedAt: string;
}

export type OfflineLookup =
  | { kind: 'found'; order: OfflineCheckinOrderDto }
  /** Already admitted before the snapshot was taken. */
  | { kind: 'already'; order: OfflineCheckinOrderDto; at: string }
  /** This device already admitted it, offline, and has not synced yet. */
  | { kind: 'queued'; order: OfflineCheckinOrderDto; queued: QueuedAdmission }
  | { kind: 'unknown' };

/**
 * Resolve a scanned or typed value against the snapshot.
 *
 * A QR is tried first and by hash, so the exact string read has to be one we issued; a typed order
 * code is tried second. The order matters: a payload that happens to look like a code must not
 * resolve as one.
 */
export async function lookupOffline(
  index: OfflineIndex,
  queue: readonly QueuedAdmission[],
  raw: string,
): Promise<OfflineLookup> {
  const value = raw.trim();
  if (!value) return { kind: 'unknown' };

  const order = index.byCredentialHash.get(await hashPayload(value))
    ?? index.byCode.get(normalizeCode(value));
  if (!order) return { kind: 'unknown' };

  const queued = queue.find((q) => normalizeCode(q.code) === normalizeCode(order.code));
  if (queued) return { kind: 'queued', order, queued };
  if (order.checkedInAt) return { kind: 'already', order, at: order.checkedInAt };
  return { kind: 'found', order };
}

export type ReplayVerdict =
  /** The server took it. The admission is a fact and leaves the queue. */
  | { kind: 'accepted'; item: QueuedAdmission }
  /**
   * The server refused because the order was already admitted — the double-admission this mode
   * cannot prevent. It leaves the queue and becomes an exception for a person.
   */
  | { kind: 'conflict'; item: QueuedAdmission; detail: string }
  /** The server refused for another reason — revoked, refunded, not paid. Also a person's problem. */
  | { kind: 'rejected'; item: QueuedAdmission; detail: string }
  /** The network is still down. It stays queued; nothing is lost and nothing is decided. */
  | { kind: 'deferred'; item: QueuedAdmission };

export interface ReplayException {
  id: string;
  code: string;
  name: string;
  admittedAt: string;
  kind: 'conflict' | 'rejected';
  detail: string;
}

export const exceptionOf = (verdict: ReplayVerdict): ReplayException | null => (
  verdict.kind === 'conflict' || verdict.kind === 'rejected'
    ? {
      id: verdict.item.id,
      code: verdict.item.code,
      name: verdict.item.name,
      admittedAt: verdict.item.admittedAt,
      kind: verdict.kind,
      detail: verdict.detail,
    }
    : null
);

/** What is left to send after a replay pass. Deferred items keep their order. */
export function remainingAfter(
  queue: readonly QueuedAdmission[],
  verdicts: readonly ReplayVerdict[],
): QueuedAdmission[] {
  const settled = new Set(verdicts.filter((v) => v.kind !== 'deferred').map((v) => v.item.id));
  return queue.filter((item) => !settled.has(item.id));
}

/* ── persistence ─────────────────────────────────────────────────────────────────────────────── */

/*
 * The queue is the part that must never be lost: it holds admissions that have happened in the
 * physical world and not yet anywhere else. The bundle is merely expensive to refetch. Both are
 * written through the same guard, because a private window, a full quota or blocked site data all
 * throw here and none of them is a reason for a gate to stop working.
 */
const KEY = (eventSlug: string | undefined, part: string) => `jamtick.offlineCheckin.${part}.${eventSlug ?? 'default'}`;

export function readStored<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Returns false when the write did not stick, so a caller can say so rather than assume. */
export function writeStored(key: string, value: unknown): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export const queueKey = (eventSlug: string | undefined) => KEY(eventSlug, 'queue');
export const bundleKey = (eventSlug: string | undefined) => KEY(eventSlug, 'bundle');
export const exceptionsKey = (eventSlug: string | undefined) => KEY(eventSlug, 'exceptions');

/** How stale a snapshot may be before the desk says it is old. Doors-open to curfew, roughly. */
export const BUNDLE_STALE_HOURS = 12;

export function bundleAgeHours(generatedAt: string, now = Date.now()): number | null {
  const at = new Date(generatedAt).getTime();
  return Number.isFinite(at) ? (now - at) / 3_600_000 : null;
}
